import type { HTTPContentTypeCategory } from '@carboncopyco/editor-utils';
import type {
  TransactionModifier,
  TransactionRule,
  TransactionSelector,
} from '@carboncopyco/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useReducer } from 'react';
import type { Transaction } from './types';
import {
  CreateTransactionRuleStep,
  type TransactionModifiersForm,
} from './types';

enum WizardActionType {
  NextStep = 0,
  PrevStep = 1,
  SelectTransaction = 2,
  UpdateTransactionRule = 3,
  SelectResponseType = 4,
  ResetWizard = 5,
}

export interface WizardState {
  stepIndex: number;
  selectedTransaction?: Transaction;
  selectedResponseType?: [
    responseType: TransactionModifiersForm['responseType'],
    contentType: HTTPContentTypeCategory,
  ];
  transactionRule: Partial<Pick<TransactionRule, 'name' | 'description'>> & {
    selectors?: TransactionSelector[];
    modifiers?: TransactionModifier[];
  };
}

interface WizardAction {
  type: WizardActionType;
  payload?: any;
}

const createTransactionRuleReducer = (
  state: WizardState,
  action: WizardAction
) => {
  const { type, payload } = action;
  switch (type) {
    case WizardActionType.NextStep: {
      return {
        ...state,
        stepIndex: state.stepIndex + 1,
      };
    }
    case WizardActionType.PrevStep: {
      return {
        ...state,
        stepIndex: state.stepIndex - 1,
      };
    }
    case WizardActionType.SelectTransaction: {
      if (state.selectedTransaction?.id !== payload?.id) {
        return {
          ...state,
          selectedTransaction: payload as Transaction,
          transactionRule: {
            name: state.transactionRule?.name,
            description: state.transactionRule?.description,
          },
        };
      }

      return state;
    }
    case WizardActionType.UpdateTransactionRule: {
      return {
        ...state,
        transactionRule: {
          ...state.transactionRule,
          ...payload,
        } as WizardState['transactionRule'],
      };
    }
    case WizardActionType.SelectResponseType: {
      return {
        ...state,
        selectedResponseType: payload as WizardState['selectedResponseType'],
      };
    }
    case WizardActionType.ResetWizard: {
      return {
        ...initialState,
      };
    }
    default: {
      throw Error(`Unknown action: ${type}`);
    }
  }
};

const initialIndex = 0;
const initialState: WizardState = {
  selectedResponseType: ['static', null],
  stepIndex: initialIndex,
  transactionRule: {},
};

export const useCreateTransactionRuleReducer = () => {
  const [
    { stepIndex, selectedTransaction, transactionRule, selectedResponseType },
    dispatch,
  ] = useReducer(createTransactionRuleReducer, initialState);
  const { editorDataRulesResponseVisualization } = useFlags();

  const nextStep = useCallback(
    () => dispatch({ type: WizardActionType.NextStep }),
    []
  );

  const prevStep = useCallback(
    () => dispatch({ type: WizardActionType.PrevStep }),
    []
  );

  const onSelectTransaction = useCallback(
    (data: WizardState['selectedTransaction']) =>
      dispatch({
        type: WizardActionType.SelectTransaction,
        payload: data,
      }),
    []
  );

  const updateTransactionRule = useCallback(
    (data: WizardState['transactionRule']) =>
      dispatch({
        type: WizardActionType.UpdateTransactionRule,
        payload: data,
      }),
    []
  );
  const onResponseTypeSelected = useCallback(
    (data: WizardState['selectedResponseType']) =>
      dispatch({
        type: WizardActionType.SelectResponseType,
        payload: data,
      }),
    []
  );

  const resetWizard = useCallback(
    () =>
      dispatch({
        type: WizardActionType.ResetWizard,
        payload: null,
      }),
    []
  );

  const steps = useMemo(() => {
    const [responseType, contentType] = selectedResponseType;

    return [
      CreateTransactionRuleStep.SelectTransaction,
      CreateTransactionRuleStep.CreateTransactionSelectors,
      CreateTransactionRuleStep.CreateTransactionModifiers,
      editorDataRulesResponseVisualization &&
        responseType === 'static' &&
        contentType === 'json' &&
        CreateTransactionRuleStep.CreateResponseVisualization,
      CreateTransactionRuleStep.GeneralSettings,
    ].filter(Boolean);
  }, [editorDataRulesResponseVisualization, selectedResponseType]);

  const step = steps[stepIndex];

  return {
    steps,
    step,
    stepIndex,
    selectedTransaction,
    selectedResponseType,
    transactionRule,
    nextStep,
    prevStep,
    onSelectTransaction,
    updateTransactionRule,
    onResponseTypeSelected,
    resetWizard,
  };
};
